import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useReducedMotion } from 'react-native-reanimated';

import { RiveAdapter, type RiveRef } from '@/components/riveAdapter';
import { RIVE_ANIMATIONS, type RiveAnimationName } from '@/constants/riveAnimations';

/**
 * How long an animation plays under reduced motion before it is paused. These intros settle
 * into their finished picture within a couple of seconds, so freezing after this leaves a
 * meaningful still, and it stays under the five seconds after which moving content needs
 * a way to be stopped.
 */
export const REDUCED_MOTION_SETTLE_MS = 3000;

type RiveAnimationProps = {
  name: RiveAnimationName;
  autoplay?: boolean;
};

/**
 * The one way a screen plays a bundled animation: it names the animation and this looks up
 * the file. Screens never import a `.riv` or build a source themselves, so everything that
 * applies to every animation, such as honoring reduced motion, lives here once.
 *
 * With reduced motion on, the animation plays once and pauses after a short settle time. The
 * native runtime cannot jump to a frame, so playing to the finished picture and holding it is
 * the only still it can reach, and starting paused would show whatever the first frame is,
 * which can be a lone dot or arc.
 */
export const RiveAnimation = forwardRef<RiveRef, RiveAnimationProps>(
  ({ name, autoplay = true }, ref) => {
    const reduceMotion = useReducedMotion();

    const adapterRef = useRef<RiveRef | null>(null);

    /**
     * The adapter's handle is recreated when its runtime finishes loading, so the screen's
     * ref and the pause below both follow the latest one instead of capturing the first.
     */
    const setAdapterRef = useCallback(
      (instance: RiveRef | null) => {
        adapterRef.current = instance;

        if (typeof ref === 'function') {
          ref(instance);
        } else if (ref) {
          ref.current = instance;
        }
      },
      [ref]
    );

    useEffect(() => {
      if (!reduceMotion || !autoplay) {
        return undefined;
      }

      const timer = setTimeout(() => {
        adapterRef.current?.pause();
      }, REDUCED_MOTION_SETTLE_MS);

      return () => clearTimeout(timer);
    }, [reduceMotion, autoplay]);

    return <RiveAdapter ref={setAdapterRef} source={RIVE_ANIMATIONS[name]} autoplay={autoplay} />;
  }
);

RiveAnimation.displayName = 'RiveAnimation';
